import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button, Tooltip } from '~/components';
import { Field, NumberInput } from '~/components/Form';
import { useIntl } from '~/hooks/useIntl';
import { srOnlyStyle } from '~/styles/utilities.css';
import { clsx } from '~/styles/utils';

import { useFormWithLinesContext } from '../../FormLines.context';
import { useSectionContext } from '../../Section/Section.context';
import { useLineContext } from '../../SectionLines/SectionLines.context';
import { useLineProduct } from '../Line.hooks';
import { productPriceNotSyncStyle, wrapperStyle } from './FormPrice.css';

export const FormPrice = () => {
  const { control, setValue } = useFormWithLinesContext();
  const { required } = useSectionContext();
  const { isMobile, lineFieldName } = useLineContext();

  const product = useLineProduct(lineFieldName);
  const productPrice = product?.price ?? null;

  const { formatCurrency } = useIntl();
  const { t } = useTranslation(['lines', 'validation']);

  return (
    <Controller
      control={control}
      name={`${lineFieldName}.price`}
      render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
        <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
          <Field.Label className={clsx(!isMobile && srOnlyStyle)}>{t('lines:price.header')}</Field.Label>

          <div className={wrapperStyle}>
            <NumberInput onBlur={onBlur} onValueChange={onChange} prefix="€" ref={ref} value={value} />

            {productPrice !== null && value !== productPrice && (
              <div className={productPriceNotSyncStyle}>
                <Tooltip content={t('lines:price.productNotSyncTooltip')}>
                  <Button
                    aria-label={t('lines:price.productNotSyncTooltip')}
                    icon="Link"
                    onClick={() => setValue(`${lineFieldName}.price`, productPrice)}
                    type="button"
                  />
                </Tooltip>
              </div>
            )}
          </div>

          {isMobile && <Field.Error match={!!error}>{error?.message}</Field.Error>}
        </Field.Root>
      )}
      rules={{
        required: { value: required, message: t('validation:required') },
        min: {
          value: required ? 0.01 : 0,
          message: t('validation:min', { attribute: t('lines:price.header'), min: formatCurrency(0.01) }),
        },
        max: {
          value: 100_000,
          message: t('validation:max', { attribute: t('lines:price.header'), max: formatCurrency(100_000) }),
        },
      }}
    />
  );
};
